/**
 * Clients Page
 * CRUD operations for client profiles with role-based access control
 */

import { AlertCircle, Loader2 } from 'lucide-react'
import { useIsAdmin } from '../hooks'
import { ClientFormModal } from './clients-page/ClientFormModal'
import { ClientsTable } from './clients-page/ClientsTable'
import { ClientsToolbar, PageHeader } from './clients-page/ClientsToolbar'
import { DeleteClientModal } from './clients-page/DeleteClientModal'
import { LinkCaseModal } from './clients-page/LinkCaseModal'
import { useClientsState, type ClientsState } from './clients-page/use-clients-state'

function LoadingSpinner(): React.ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#4e5e82]" />
    </div>
  )
}

function ErrorBanner({ error }: { error: string }): React.ReactElement {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    </div>
  )
}

interface ModalsProps {
  state: ClientsState
  isAdmin: boolean
}

function ClientsModals({ state, isAdmin }: ModalsProps): React.ReactElement {
  const { modal, form, del, link, actions, profiles } = state
  return (
    <>
      {(modal.showCreateModal || modal.showEditModal) && (
        <ClientFormModal
          isCreate={modal.showCreateModal}
          formData={form.formData}
          setFormData={form.setFormData}
          formError={form.formError}
          formLoading={form.formLoading}
          isAdmin={isAdmin}
          onClose={actions.closeFormModal}
          onSubmit={(event) => {
            void (modal.showCreateModal ? actions.handleCreate(event) : actions.handleUpdate(event))
          }}
        />
      )}
      {modal.showDeleteModal && modal.selectedClient && (
        <DeleteClientModal
          selectedClient={modal.selectedClient}
          profiles={profiles}
          deleteOption={del.deleteOption}
          setDeleteOption={del.setDeleteOption}
          reassignTargetId={del.reassignTargetId}
          setReassignTargetId={del.setReassignTargetId}
          formError={form.formError}
          formLoading={form.formLoading}
          onClose={actions.closeDeleteModal}
          onConfirm={() => {
            void actions.handleDelete()
          }}
        />
      )}
      {modal.showLinkCaseModal && modal.selectedClient && (
        <LinkCaseModal
          selectedClient={modal.selectedClient}
          unlinkedCases={link.unlinkedCases}
          unlinkedCasesLoading={link.unlinkedCasesLoading}
          selectedCaseToLink={link.selectedCaseToLink}
          setSelectedCaseToLink={link.setSelectedCaseToLink}
          formError={form.formError}
          formLoading={form.formLoading}
          onClose={actions.closeLinkModal}
          onConfirm={() => {
            void actions.handleLinkCase()
          }}
        />
      )}
    </>
  )
}

interface ClientsBodyProps {
  state: ClientsState
  isAdmin: boolean
}

function ClientsBody({ state, isAdmin }: ClientsBodyProps): React.ReactElement {
  const { profiles, error, searchQuery, setSearchQuery, statusFilter, setStatusFilter } = state
  const { modal, actions } = state
  return (
    <div className="space-y-6">
      <PageHeader onNew={actions.openCreateModal} />
      {error && <ErrorBanner error={error} />}
      <ClientsToolbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />
      <ClientsTable
        profiles={profiles}
        hasFilter={Boolean(searchQuery || statusFilter)}
        rowProps={{
          isAdmin,
          actionMenuClient: modal.actionMenuClient,
          setActionMenuClient: modal.setActionMenuClient,
          onEdit: actions.openEditModal,
          onLink: (client) => {
            void actions.openLinkCaseModal(client)
          },
          onDelete: actions.openDeleteModal,
          onViewCases: actions.handleViewClientCases,
        }}
      />
      <ClientsModals state={state} isAdmin={isAdmin} />
      {modal.actionMenuClient && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            modal.setActionMenuClient(null)
          }}
        />
      )}
    </div>
  )
}

export function ClientsPage(): React.ReactElement {
  const isAdmin = useIsAdmin()
  const state = useClientsState({ isAdmin })
  if (state.loading) return <LoadingSpinner />
  return <ClientsBody state={state} isAdmin={isAdmin} />
}
